import { fmtAge } from "@chula/shared";
import type { ReactNode } from "react";

/**
 * PanelState — the one pattern for a panel body that has nothing (or only
 * old data) to show. Sits under a <PanelHeader>, which already carries the
 * tier badge; this line says, in plain words, what the operator is looking at.
 *
 *   loading  skeleton lines + "Loading …" for screen readers (never zeros)
 *   empty    the feed answered and had nothing — say so, name the source
 *   error    the feed failed — no fake numbers; Retry when we can refetch
 *   stale    old data is still shown — say how old
 *
 * Zero is a value; unknown is not zero. See .claude/skills/ui-review §3.
 */
export type PanelStateKind = "loading" | "empty" | "error" | "stale";

interface Props {
  kind: PanelStateKind;
  /** What the panel holds, lower case — "traffic cameras", "air stations". */
  what: string;
  /** Source label for the honest empty / error line. */
  source?: string;
  /** Age of the data still on screen (stale) or last good data (error). */
  ageMinutes?: number | null;
  /** Refetch hook — renders a Retry button on error. */
  onRetry?: () => void;
  /** Override the default sentence. */
  children?: ReactNode;
}

function defaultMessage({ kind, what, source, ageMinutes }: Props): string {
  const from = source ? ` from ${source}` : "";
  const age = ageMinutes != null && ageMinutes >= 1 ? fmtAge(ageMinutes) : null;
  switch (kind) {
    case "loading":
      return `Loading ${what}…`;
    case "empty":
      return `No ${what} in this feed right now${from}.`;
    case "error":
      return age
        ? `Source offline — no current ${what}${from}. Last good data ${age} ago.`
        : `Source offline — no current ${what}${from}.`;
    case "stale":
      return age ? `Showing last known ${what} — ${age} old.` : `Showing last known ${what}.`;
  }
}

export function PanelState(props: Props) {
  const { kind, onRetry, children } = props;
  const message = children ?? defaultMessage(props);

  if (kind === "loading") {
    // The sentence is VISIBLE. A panel that shows only a pale skeleton claims
    // space and explains nothing — to the eye it reads as an empty hole, and
    // a request that never answers leaves that hole on screen forever. One
    // skeleton bar carries "pending"; the words carry "what".
    return (
      <div className="panel-state panel-state--loading caption mono" role="status" aria-live="polite">
        <span className="skeleton panel-state-bar" aria-hidden />
        <span className="panel-state-msg">{message}</span>
      </div>
    );
  }

  return (
    <div
      className={`panel-state panel-state--${kind} caption mono`}
      role="status"
    >
      <span className="panel-state-msg">{message}</span>
      {kind === "error" && onRetry && (
        <button type="button" className="panel-state-retry mono" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Pick the state for a list feed. `null` means "render the data".
 * Stale = a cache-tier copy at least STALE_AFTER_MIN old.
 */
export const STALE_AFTER_MIN = 30;

export function listFeedState(opts: {
  loading?: boolean;
  count: number;
  tier?: string | null;
  ageMinutes?: number | null;
}): PanelStateKind | null {
  const { loading, count, tier, ageMinutes } = opts;
  if (loading && count === 0) return "loading";
  if (tier === "unavailable" && count === 0) return "error";
  if (count === 0) return "empty";
  if (tier === "cache" && ageMinutes != null && ageMinutes >= STALE_AFTER_MIN) return "stale";
  return null;
}
